import { Editor, Element, Node, Path, Range, Text, Transforms } from 'slate';

/**
 * Slate 气泡（mention）共享工具：SlatePromptEditor / SlateStoryboardEditor 共用
 *
 * 用户侧统一认 @资产名（手打 / 粘贴 / 分镜大师输出），内部存储格式不变，仍是 @「资产名」
 * 支持识别：
 *   1. @「资产名」 —— 存储格式，立即转气泡
 *   2. @资产名     —— 按素材标题「最长优先」匹配，避免「中哥哥」被「中哥」截断
 */

// 气泡缩略图：优先迷你缩略图，兼容 camelCase / snake_case 两种字段
export const getAssetThumb = (asset) =>
  asset?.miniThumbnailUrl || asset?.mini_thumbnail_url ||
  asset?.thumbnailUrl || asset?.thumbnail_url ||
  asset?.thumbUrl || asset?.url || null;

export const buildMentionNode = (asset) => ({
  type: 'mention',
  title: asset.title,
  // 提示词中的资产引用是文本标记不是实时引用，不存 assetId
  assetType: asset.type,
  category: asset.category || 'prop',
  thumbUrl: getAssetThumb(asset),
  children: [{ text: '' }],
});

/**
 * 整理可匹配的素材：排除咒语(text)/工作流(default)，按标题去重，标题从长到短排序
 */
export const prepareMentionAssets = (assets = []) => {
  const seen = new Set();
  const list = [];
  for (const a of assets) {
    const title = (a?.title || '').trim();
    if (!title || a.type === 'text' || a.category === 'default') continue;
    if (seen.has(title)) continue;
    seen.add(title);
    list.push({ ...a, title });
  }
  return list.sort((a, b) => b.title.length - a.title.length);
};

// 素材列表签名：标题+缩略图不变就不重新匹配（避免父组件每次渲染新建数组引发重复归一化）
export const getAssetsSignature = (assets = []) =>
  assets.map(a => `${a.title}|${getAssetThumb(a) || ''}`).join('\n');

/**
 * 在一段文本里找出所有 @ 提及
 * @param {string} text
 * @param {Array} sortedAssets - prepareMentionAssets 的结果
 * @param {Object} opts.allowBareBracket - 兼容没有 @ 的 「资产名」 写法
 * @returns {Array<{start, end, asset, bracketed}>}
 */
export const findMentionMatches = (text, sortedAssets, { allowBareBracket = false } = {}) => {
  const matches = [];
  if (!text || !sortedAssets?.length) return matches;

  let i = 0;
  while (i < text.length) {
    const ch = text[i];
    const isAt = ch === '@';
    const isBareBracket = allowBareBracket && ch === '「';
    if (!isAt && !isBareBracket) { i++; continue; }

    // ① @「资产名」 / 「资产名」
    const bracketStart = isAt ? i + 1 : i;
    if (text[bracketStart] === '「') {
      const close = text.indexOf('」', bracketStart + 1);
      if (close > bracketStart + 1) {
        const title = text.slice(bracketStart + 1, close);
        const asset = sortedAssets.find(a => a.title === title);
        if (asset) {
          matches.push({ start: i, end: close + 1, asset, bracketed: true });
          i = close + 1;
          continue;
        }
      }
    }

    // ② @资产名（最长优先）
    if (isAt) {
      const titleStart = i + 1;
      const asset = sortedAssets.find(a => text.startsWith(a.title, titleStart));
      if (asset) {
        const end = i + 1 + asset.title.length;
        matches.push({ start: i, end, asset, bracketed: false });
        i = end;
        continue;
      }
    }
    i++;
  }
  return matches;
};

/**
 * 把一行纯文本转成 Slate 行内 children（文本 + mention 节点）
 */
export const parseLineToChildren = (line, sortedAssets, opts) => {
  const children = [];
  let last = 0;
  for (const m of findMentionMatches(line, sortedAssets, opts)) {
    if (m.start > last) children.push({ text: line.slice(last, m.start) });
    children.push(buildMentionNode(m.asset));
    last = m.end;
  }
  if (last < line.length) children.push({ text: line.slice(last) });
  // Slate 要求行内 void 节点两侧都有文本节点（否则光标进不去气泡前后）
  if (children.length === 0 || children[children.length - 1].type) children.push({ text: '' });
  if (children[0].type) children.unshift({ text: '' });
  return children;
};

/**
 * Slate 插件：文本里出现 @资产名 时自动转成气泡
 *
 * 打字时，光标紧贴在一个未加括号的 @资产名 末尾就先不转——
 * 用户可能还在继续输入更长的名字（@中哥 → @中哥哥）。
 * 继续输入、移开光标或失焦后（forceConvertMentions）才转。
 *
 * 使用方需设置 editor.mentionAssets = prepareMentionAssets(...)
 */
export const withAutoMentions = (editor) => {
  const { normalizeNode } = editor;
  editor.mentionAssets = [];
  editor.forceMentionConvert = false;

  editor.normalizeNode = (entry) => {
    const [node, path] = entry;

    if (Text.isText(node) && node.text && editor.mentionAssets.length && path.length > 1) {
      const parent = Node.parent(editor, path);
      // 只处理普通段落里的文本，mention / 分镜标题等 void 节点内部的空文本跳过
      if (Element.isElement(parent) && !editor.isVoid(parent)) {
        const { selection } = editor;
        const match = findMentionMatches(node.text, editor.mentionAssets).find((m) => {
          if (m.bracketed || editor.forceMentionConvert) return true;
          const cursorAtEnd = selection && Range.isCollapsed(selection) &&
            Path.equals(selection.anchor.path, path) && selection.anchor.offset === m.end;
          return !cursorAtEnd;
        });

        if (match) {
          Editor.withoutNormalizing(editor, () => {
            Transforms.delete(editor, {
              at: { anchor: { path, offset: match.start }, focus: { path, offset: match.end } },
            });
            Transforms.insertNodes(editor, buildMentionNode(match.asset), {
              at: { path, offset: match.start },
            });
          });
          return; // 变更后 Slate 会对新节点重新归一化，剩余的 @ 继续在下一轮处理
        }
      }
    }

    normalizeNode(entry);
  };

  return editor;
};

/**
 * 强制全量转换（加载 / 外部文本写入 / 素材到位 / 失焦时调用），无视光标位置
 */
export const forceConvertMentions = (editor) => {
  if (!editor.mentionAssets?.length) return;
  editor.forceMentionConvert = true;
  try {
    Editor.normalize(editor, { force: true });
  } finally {
    editor.forceMentionConvert = false;
  }
};

/**
 * 将 Slate value（children 数组）转成纯文本，mention 节点输出为 @「标题」
 */
export const slateValueToText = (children) => {
  if (!children) return '';
  return children
    .map((node) => {
      if (node.type === 'paragraph') {
        return node.children
          .map((child) => {
            if (child.type === 'mention') {
              return `@「${child.title}」`;
            }
            return child.text || '';
          })
          .join('');
      }
      if (node.type === 'mention') {
        return `@「${node.title}」`;
      }
      return node.text || '';
    })
    .join('\n');
};
